import { useEffect, useState } from "react";
import { TextButton } from "../../ui/buttons/TextButton/TextButton";
import { TextInput } from "../../ui/inputs/TextInput/TextInput";
import { ButtonWrap } from "../../ui/wrappers/ButtonWrap/ButtonWrap";
import ModalWrap from "../../ui/wrappers/ModalWrap/ModalWrap";
import "./CollectionModal.css";
import { TextError } from "../../ui/errors/TextError/TextError";
import LoadBlock from "../LoadBlock/LoadBlock";
import {
  useCreateCollectionMutation,
  useGetCollectionByIdQuery,
  useUpdateCollectionMutation,
} from "../../../store/collections/collectionsApi";

interface FormErrors {
  name?: string;
}

interface CollectionModalProps {
  uuidItem?: string;
  closeModal: () => void;
  updatePage: () => void;
}

export const CollectionModal = ({
  uuidItem,
  closeModal,
  updatePage,
}: CollectionModalProps) => {
  const [name, setName] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [createCollection, { isLoading: isCreating, error: createError }] =
    useCreateCollectionMutation();
  const [updateCollection, { isLoading: isUpdating, error: updateError }] =
    useUpdateCollectionMutation();
  const { data: fullCollection, isLoading: isFullLoading } =
    useGetCollectionByIdQuery(uuidItem ?? "", {
      skip: !uuidItem,
      refetchOnMountOrArgChange: true,
    });

  const handleClose = () => {
    updatePage();
    closeModal();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);

    if (!value) {
      setFormErrors((prev) => ({
        ...prev,
        name: "Название не может быть пустым",
      }));
    } else {
      setFormErrors((prev) => {
        const { name, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (uuidItem) {
        await updateCollection({ uuid: uuidItem, name }).unwrap();
      } else {
        await createCollection({ name }).unwrap();
      }
      updatePage();
      closeModal();
    } catch (err) {
      console.error("Ошибка при сохранении коллекции", err);
    }
  };

  useEffect(() => {
    if (uuidItem && fullCollection) {
      setName(fullCollection.name);
    } else if (!uuidItem) {
      setName("");
    }
    setFormErrors({});
  }, [uuidItem, fullCollection]);

  return (
    <ModalWrap
      action={handleClose}
      title={`${uuidItem ? "Редактировать" : "Добавить новую"} коллекцию`}
    >
      {isFullLoading ? (
        <LoadBlock />
      ) : (
        <form className="collection-modal" onSubmit={handleSubmit}>
          <TextInput
            name="name"
            label="Название коллекции"
            width="30rem"
            margin="2rem 0 0 0"
            value={name}
            action={handleNameChange}
            textError={formErrors.name}
          />
          <ButtonWrap>
            <TextButton
              type="submit"
              text="Сохранить"
              color="black"
              margin="2rem 0 0 0"
              disabled={Object.keys(formErrors).length > 0 || !name}
              preloader={isCreating || isUpdating}
            />
            <TextButton
              type="button"
              text="Отмена"
              color="white"
              margin="2rem 0 0 0"
              action={handleClose}
            />
          </ButtonWrap>
        </form>
      )}
      <TextError text={JSON.stringify(createError || updateError)} />
    </ModalWrap>
  );
};
